import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Video, Shirt, Camera, ArrowLeft, ArrowRight, Award, Clock, DollarSign,
  CheckCircle, ChevronDown, ChevronUp, Music, Theater
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const pathwayIcons = {
  FILM: Video,
  FASHION: Shirt,
  CONTENT: Camera,
  MUSIC: Music,
  THEATER: Theater
};

const pathwayColors = {
  FILM: { bg: "bg-violet-500", gradient: "from-violet-500 to-purple-600", text: "text-violet-400" },
  FASHION: { bg: "bg-pink-500", gradient: "from-pink-500 to-rose-600", text: "text-pink-400" },
  CONTENT: { bg: "bg-cyan-500", gradient: "from-cyan-500 to-blue-600", text: "text-cyan-400" },
  MUSIC: { bg: "bg-amber-500", gradient: "from-amber-500 to-orange-600", text: "text-amber-400" },
  THEATER: { bg: "bg-red-500", gradient: "from-red-500 to-rose-700", text: "text-red-400" }
};

export default function CreativePathways() {
  const { pathwayId } = useParams();
  const [pathways, setPathways] = useState([]);
  const [selectedPathway, setSelectedPathway] = useState(null);
  const [expandedTrack, setExpandedTrack] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPathways();
  }, []);

  useEffect(() => {
    if (pathwayId && pathways.length) {
      const pathway = pathways.find(p => p.id === pathwayId);
      setSelectedPathway(pathway);
    }
  }, [pathwayId, pathways]);

  const fetchPathways = async () => {
    try {
      const res = await fetch(`${API}/api/studio/pathways`);
      if (res.ok) {
        const data = await res.json();
        setPathways(data.pathways || []);
      }
    } catch (err) {
      console.error('Error fetching pathways:', err);
    } finally {
      setLoading(false);
    }
  };

  // Pathway Detail View
  if (selectedPathway) {
    const Icon = pathwayIcons[selectedPathway.id] || Camera;
    const colors = pathwayColors[selectedPathway.id] || pathwayColors.CONTENT;

    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-900 to-violet-950" data-testid="pathway-detail">
        {/* Header */}
        <div className={`py-16 bg-gradient-to-r ${colors.gradient}`}>
          <div className="container mx-auto px-4">
            <Link to="/legacystudio/pathways" className="inline-flex items-center text-white/80 hover:text-white mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" /> All Pathways
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur">
                <Icon className="h-10 w-10 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-white">{selectedPathway.name}</h1>
                <p className="text-white/80">{selectedPathway.tracks?.length} Career Tracks</p>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          {/* Certifications */}
          {selectedPathway.certifications?.length > 0 && (
            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Award className="h-5 w-5 mr-2 text-yellow-400" />
                Industry Certifications
              </h2>
              <div className="flex flex-wrap gap-3">
                {selectedPathway.certifications.map((cert, idx) => (
                  <Badge key={`item-${idx}`} className="bg-yellow-500/20 text-yellow-300 border-yellow-400 py-2 px-4">
                    {cert}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Career Tracks */}
          <h2 className="text-2xl font-bold text-white mb-6">Career Tracks</h2>
          <div className="space-y-4">
            {selectedPathway.tracks?.map((track, idx) => (
              <motion.div
                key={track.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card 
                  className={`bg-white/5 backdrop-blur border-white/10 hover:border-violet-400/50 transition-all cursor-pointer ${
                    expandedTrack === track.id ? 'border-violet-400' : ''
                  }`}
                  onClick={() => setExpandedTrack(expandedTrack === track.id ? null : track.id)}
                >
                  <CardContent className="py-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${colors.bg}/20`}>
                          <Icon className={`h-6 w-6 ${colors.text}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white text-lg">{track.name}</h3>
                          <p className="text-violet-300 text-sm">{track.roles}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right hidden sm:block">
                          <div className="flex items-center text-emerald-400 text-sm">
                            <DollarSign className="h-4 w-4 mr-1" />
                            {track.wage_range}
                          </div>
                          <div className="flex items-center text-violet-300 text-xs mt-1">
                            <Clock className="h-3 w-3 mr-1" />
                            {track.credential_time}
                          </div>
                        </div>
                        {expandedTrack === track.id ? (
                          <ChevronUp className="h-5 w-5 text-violet-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-violet-400" />
                        )}
                      </div>
                    </div>

                    {expandedTrack === track.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-6 pt-6 border-t border-white/10"
                      >
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="text-sm font-semibold text-violet-300 mb-3">Career Progression</h4>
                            <div className="flex items-center gap-2 flex-wrap">
                              {track.roles?.split(' → ').map((role, rIdx, arr) => (
                                <div key={rIdx} className="flex items-center">
                                  <Badge className="bg-violet-500/20 text-violet-200 border-violet-400">
                                    {role}
                                  </Badge>
                                  {rIdx < arr.length - 1 && (
                                    <ArrowRight className="h-4 w-4 text-violet-400 mx-2" />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div>
                              <p className="text-xs text-violet-400 uppercase tracking-wider mb-1">Salary Range</p>
                              <p className="text-emerald-400 font-medium">{track.wage_range}/year</p>
                            </div>
                            <div>
                              <p className="text-xs text-violet-400 uppercase tracking-wider mb-1">Time to Credential</p>
                              <p className="text-white">{track.credential_time}</p>
                            </div>
                          </div>
                        </div>
                        <div className="mt-6 flex gap-3">
                          <Link to="/legacystudio/portfolio">
                            <Button className="bg-violet-600 hover:bg-violet-700">
                              Build Your Portfolio
                            </Button>
                          </Link>
                          <Link to="/legacystudio/productions">
                            <Button variant="outline" className="border-violet-400 text-violet-200 hover:bg-violet-500/20">
                              Find Opportunities
                            </Button>
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Pathways List View
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-900 to-violet-950" data-testid="creative-pathways">
      {/* Header */}
      <div className="border-b border-violet-500/30 py-16">
        <div className="container mx-auto px-4">
          <Link to="/legacystudio" className="inline-flex items-center text-violet-300 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to LegacyStudio
          </Link>
          <h1 className="text-4xl font-bold text-white mb-4">Creative Pathways</h1>
          <p className="text-violet-200 max-w-2xl">
            Explore structured career paths in film, fashion, and digital content. 
            Each pathway includes industry certifications, mentorship opportunities, and real-world experience.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          {pathways.map((pathway, idx) => {
            const Icon = pathwayIcons[pathway.id] || Camera;
            const colors = pathwayColors[pathway.id] || pathwayColors.CONTENT;

            return (
              <motion.div
                key={pathway.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.15 }}
              >
                <Link to={`/legacystudio/pathways/${pathway.id}`}>
                  <Card className="h-full bg-white/5 backdrop-blur border-white/10 hover:border-violet-400/50 transition-all group cursor-pointer overflow-hidden">
                    <div className={`h-3 bg-gradient-to-r ${colors.gradient}`} />
                    <CardHeader className="pt-6">
                      <div className={`p-4 rounded-xl bg-gradient-to-br ${colors.gradient} text-white w-fit mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon className="h-8 w-8" />
                      </div>
                      <CardTitle className="text-white text-2xl">{pathway.name}</CardTitle>
                      <CardDescription className="text-violet-200">
                        {pathway.tracks?.length} career tracks to explore
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 mb-6">
                        {pathway.tracks?.slice(0, 4).map(track => (
                          <div key={track.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                            <span className="text-violet-100">{track.name}</span>
                            <span className="text-emerald-400">{track.wage_range}</span>
                          </div>
                        ))}
                        {pathway.tracks?.length > 4 && (
                          <p className="text-violet-400 text-xs">+{pathway.tracks.length - 4} more tracks</p>
                        )}
                      </div>

                      {pathway.certifications?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs text-violet-400 mb-2">Certifications:</p>
                          <div className="flex flex-wrap gap-2">
                            {pathway.certifications.slice(0, 2).map((cert, cIdx) => (
                              <Badge key={cIdx} variant="outline" className="border-violet-400 text-violet-200 text-xs">
                                {cert}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <span className={`${colors.text} text-sm font-medium flex items-center`}>
                        Explore Pathway <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
